import Toast from 'react-native-toast-message';
import i18n from '../locales';
import { captureError } from '../config/sentry';

export type ErrorContext =
  | 'CreateListing'
  | 'UpdateListing'
  | 'DeleteListing'
  | 'LoadItem'
  | 'LoadItems'
  | 'LoadProfile'
  | 'Authentication'
  | 'General';

/**
 * Centralized error handler for API and authentication errors
 * Automatically detects session expiry and shows appropriate toast notifications
 */
export const handleApiError = (error: any, context: ErrorContext = 'General'): void => {
  console.error(`[${context}] Error:`, error);

  // Capture error in Sentry for visibility (even handled errors)
  captureError(error, context);

  // Check for JWT/session expiry errors
  const isSessionExpired =
    error.message?.includes('JWT') ||
    error.message?.includes('expired') ||
    error.message?.includes('refresh_token_not_found') ||
    error.message?.includes('not authenticated') ||
    error.code === 'PGRST301' || // PostgREST JWT expired code
    error.status === 401; // Unauthorized

  if (isSessionExpired) {
    Toast.show({
      type: 'error',
      text1: i18n.t('common.errors.sessionExpired'),
      text2: i18n.t('common.errors.sessionExpiredMessage'),
      visibilityTime: 5000,
      position: 'bottom',
      autoHide: true,
    });
    return;
  }

  // Check for network errors
  const isNetworkError =
    error.message?.includes('network') ||
    error.message?.includes('fetch') ||
    !error.message;

  if (isNetworkError) {
    Toast.show({
      type: 'error',
      text1: i18n.t('common.errors.network'),
      text2: i18n.t('common.errors.checkConnection'),
      visibilityTime: 4000,
      position: 'bottom',
    });
    return;
  }

  // Generic error toast with context-specific message
  const errorMessage = error.message || i18n.t('common.errors.unknown');

  Toast.show({
    type: 'error',
    text1: i18n.t('common.errors.title'),
    text2: errorMessage,
    visibilityTime: 4000,
    position: 'bottom',
  });
};

/**
 * Show a success toast notification
 */
export const showSuccessToast = (message: string, subtitle?: string): void => {
  Toast.show({
    type: 'success',
    text1: message,
    text2: subtitle,
    visibilityTime: 3000,
    position: 'bottom',
  });
};

/**
 * Show an info toast notification
 */
export const showInfoToast = (message: string, subtitle?: string): void => {
  Toast.show({
    type: 'info',
    text1: message,
    text2: subtitle,
    visibilityTime: 3000,
    position: 'bottom',
  });
};
